import logger from '../utils/logger';

const shareLogger = logger.module('ShareService');

export interface ShareData {
  title: string;
  text?: string;
  url?: string;
  files?: File[];
}

export interface ExerciseShareData {
  exerciseId: string;
  title: string;
  type: string;
  date: string | number;
  duration?: number;
  location?: {
    name: string;
    address?: string;
  };
  note?: string;
  photos?: string[];
  owner: {
    username: string;
    photoURL?: string;
  };
}

export interface RoutineShareData {
  routineKey: string;
  title: string;
  description?: string;
  author: string;
  tags: string[];
  coverPhoto?: string;
}

/**
 * Share service for web-based sharing functionality
 * Implements both Web Share API and fallback mechanisms
 */
class ShareService {
  private supportsWebShare: boolean;

  constructor() {
    this.supportsWebShare = 'share' in navigator && typeof navigator.share === 'function';
  }

  /**
   * Share using Web Share API or fallback
   */
  async share(data: ShareData): Promise<boolean> {
    try {
      if (this.supportsWebShare) {
        await navigator.share(data);
        shareLogger.info('Shared via Web Share API');
        return true;
      } else {
        return this.fallbackShare(data);
      }
    } catch (error) {
      shareLogger.error('Share failed:', error);
      return this.fallbackShare(data);
    }
  }

  /**
   * Share an exercise with formatted data
   */
  async shareExercise(exerciseData: ExerciseShareData): Promise<boolean> {
    const shareText = this.formatExerciseShareText(exerciseData);
    const shareUrl = this.generateExerciseShareUrl(exerciseData);

    const shareData: ShareData = {
      title: `${exerciseData.owner.username}'s ${exerciseData.title}`,
      text: shareText,
      url: shareUrl
    };

    return this.share(shareData);
  }

  /**
   * Share a workout routine
   */
  async shareRoutine(routineData: RoutineShareData): Promise<boolean> {
    const shareText = this.formatRoutineShareText(routineData);
    const shareUrl = this.generateRoutineShareUrl(routineData);

    const shareData: ShareData = {
      title: routineData.title,
      text: shareText,
      url: shareUrl
    };

    return this.share(shareData);
  }

  /**
   * Copy to clipboard as fallback
   */
  private async fallbackShare(data: ShareData): Promise<boolean> {
    try {
      const textToCopy = this.formatFallbackText(data);
      
      if ('clipboard' in navigator) {
        await navigator.clipboard.writeText(textToCopy);
        this.showCopyNotification();
        shareLogger.info('Copied to clipboard');
        return true;
      } else {
        // Fallback for older browsers
        this.legacyCopyToClipboard(textToCopy);
        this.showCopyNotification();
        return true;
      }
    } catch (error) {
      shareLogger.error('Fallback share failed:', error);
      return false;
    }
  }

  /**
   * Format exercise data for sharing
   */
  private formatExerciseShareText(exercise: ExerciseShareData): string {
    const date = typeof exercise.date === 'number' 
      ? new Date(exercise.date).toLocaleDateString()
      : new Date(exercise.date).toLocaleDateString();
    
    let text = `Just completed: ${exercise.title}\n`;
    text += `📅 ${date}\n`;
    
    if (exercise.duration) {
      const hours = Math.floor(exercise.duration / 60);
      const minutes = exercise.duration % 60;
      if (hours > 0) {
        text += `⏱️ ${hours}h ${minutes}m\n`;
      } else {
        text += `⏱️ ${minutes}m\n`;
      }
    }
    
    if (exercise.location?.name) {
      text += `📍 ${exercise.location.name}\n`;
    }
    
    if (exercise.note) {
      text += `\n${exercise.note}\n`;
    }
    
    text += `\nShared via Marune 🥋`;
    
    return text;
  }

  /**
   * Format routine data for sharing
   */
  private formatRoutineShareText(routine: RoutineShareData): string {
    let text = `Check out this workout routine: ${routine.title}\n`;
    
    if (routine.description) {
      text += `\n${routine.description}\n`;
    }
    
    if (routine.tags.length > 0) {
      text += `\nTags: ${routine.tags.map(tag => `#${tag}`).join(' ')}\n`;
    }
    
    text += `\nShared via Marune 🥋`;
    
    return text;
  }

  /**
   * Generate shareable URL for exercise
   */
  private generateExerciseShareUrl(exercise: ExerciseShareData): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/exercises/${exercise.exerciseId}`;
  }

  /**
   * Generate shareable URL for routine
   */
  private generateRoutineShareUrl(routine: RoutineShareData): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/workout-routines/${routine.routineKey}`;
  }

  /**
   * Format text for fallback sharing
   */
  private formatFallbackText(data: ShareData): string {
    let text = data.title;
    
    if (data.text) {
      text += `\n\n${data.text}`;
    }
    
    if (data.url) {
      text += `\n\n${data.url}`;
    }
    
    return text;
  }

  /**
   * Legacy clipboard copy for older browsers
   */
  private legacyCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
    } catch (error) {
      shareLogger.error('Legacy copy failed:', error);
    }
    
    document.body.removeChild(textArea);
  }

  /**
   * Show notification that content was copied
   */
  private showCopyNotification(): void {
    // This could be enhanced with a toast notification system
    // For now, we'll use a simple alert as fallback
    if (typeof window !== 'undefined') {
      // Try to use a more sophisticated notification if available
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Marune', {
          body: 'Link copied to clipboard!',
          icon: '/img/logo192.png',
          tag: 'share-copy'
        });
      } else {
        // Fallback to console log for development
        shareLogger.info('Content copied to clipboard');
      }
    }
  }

  /**
   * Generate a share card image (for future implementation)
   */
  async generateShareCard(data: ExerciseShareData): Promise<string | null> {
    // This would generate a visual share card similar to the original app
    // For now, return null - can be implemented later with Canvas API
    shareLogger.info('Share card generation not yet implemented');
    return null;
  }

  /**
   * Check if Web Share API is supported
   */
  isWebShareSupported(): boolean {
    return this.supportsWebShare;
  }

  /**
   * Check if specific share targets are supported
   */
  canShareFiles(): boolean {
    return this.supportsWebShare && 'canShare' in navigator && navigator.canShare({ files: [] });
  }
}

// Export singleton instance
const shareService = new ShareService();
export default shareService;